import { useRef, useEffect } from 'react'
import { useProvisionLogs, useProvisionLogsByTenant } from '@/hooks/useProvisionLogs'
import { Skeleton } from '@/components/ui/Skeleton'
import type { LogLevel } from '@/types'

interface ProvisionLogViewerProps {
  provisionId?: string | null
  tenantId?: string | null
  maxHeight?: string
}

const LEVEL_COLORS: Record<LogLevel, { color: string; bg: string }> = {
  info: { color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)' },
  warn: { color: 'var(--status-warning)', bg: 'var(--status-warning-dim)' },
  error: { color: 'var(--status-unhealthy)', bg: 'var(--status-unhealthy-dim)' },
  milestone: { color: 'var(--status-provisioning)', bg: 'var(--status-provisioning-dim)' },
  debug: { color: 'var(--text-tertiary)', bg: 'var(--bg-tertiary)' },
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts.replace(' ', 'T'))
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return '--:--:--'
  }
}

export function ProvisionLogViewer({ provisionId, tenantId, maxHeight = '320px' }: ProvisionLogViewerProps) {
  const { data: logsByProvision = [], isLoading: isLoadingByProvision } = useProvisionLogs(provisionId)
  const { data: logsByTenant = [], isLoading: isLoadingByTenant } = useProvisionLogsByTenant(!provisionId ? tenantId : null)
  const logs = provisionId ? logsByProvision : logsByTenant
  const isLoading = provisionId ? isLoadingByProvision : isLoadingByTenant
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [logs.length])

  if (!provisionId && !tenantId) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
        No active provision to show logs for.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={16} width={`${60 + Math.random() * 30}%`} />
        ))}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        maxHeight,
        overflowY: 'auto',
        backgroundColor: 'var(--bg-primary, #0a0a0a)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        fontFamily: 'var(--font-mono)',
        fontSize: '12px',
        lineHeight: '1.6',
        padding: '12px',
      }}
    >
      {logs.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '16px 0' }}>
          Waiting for log entries...
        </div>
      ) : (
        logs
          // Filter out milestone "started" entries — only show the completed ones
          .filter(log => !(log.level === 'milestone' && log.milestone_started && !log.milestone_ended))
          .map((log, i) => {
          const levelStyle = LEVEL_COLORS[log.level] ?? LEVEL_COLORS.info
          return (
            <div
              key={log.id ?? i}
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
                padding: '2px 0',
                opacity: log.level === 'debug' ? 0.6 : 1,
              }}
            >
              <span style={{ color: 'var(--text-tertiary)', flexShrink: 0, fontSize: '11px', minWidth: '60px' }}>
                {formatTimestamp(log.timestamp)}
              </span>
              <span style={{
                display: 'inline-block',
                padding: '0 5px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: levelStyle.color,
                backgroundColor: levelStyle.bg,
                flexShrink: 0,
                minWidth: '52px',
                textAlign: 'center',
                lineHeight: '18px',
              }}>
                {log.level}
              </span>
              <span style={{
                color: log.level === 'error'
                  ? 'var(--status-unhealthy)'
                  : log.level === 'warn'
                    ? 'var(--status-warning)'
                    : log.level === 'milestone'
                      ? 'var(--status-provisioning)'
                      : 'var(--text-secondary)',
                wordBreak: 'break-word',
              }}>
                {log.message}
              </span>
            </div>
          )
        })
      )}
    </div>
  )
}
